import {useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import dayjs from 'dayjs';
import {Avatar} from '@ui/atoms/avatar';
import {Body1, Body2} from '@ui/atoms/typography';
import {CustomMarkdown} from '@ui/atoms/typography/markdown';
import {Button} from '@ui/atoms/button';
import {DropdownVertical} from '@ui/molecules/dropdown-vertical';
import {DropdownItem} from '@pages/dashboard/you/you-page.styles';
import {MoreVerticalIcon2} from '@ui/atoms/icons/more-vertical';
import {ItemLoader} from '@ui/organisms/item-loader';
import {
  ICheckinCycleDetail,
  ISpecificCheckinsWithResponseData,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {parseName} from '../my-checkins-list';
import {buildTeamFeedAnswers, TeamFeedAnswer} from '../team-feed.utils';
import {ManagedSubmission} from '../../team-management/team-management.utils';
import {TeamFeedComments} from './team-feed-comments';
import {GoalReflectionResponse} from './goal-reflection-response';

type TeamFeedCardSubmission = ManagedSubmission & {
  isReviewed?: boolean;
  dueDay?: string;
};

interface TeamFeedResponseCardProps {
  submission: TeamFeedCardSubmission;
  mentionableUserIds: string[];
  detail?: ISpecificCheckinsWithResponseData | null;
  cycle?: ICheckinCycleDetail | null;
  isLoadingDetail?: boolean;
  detailError?: boolean;
  canManageResponses?: boolean;
  onRetryDetail: () => void;
  defaultCommentsOpen?: boolean;
}

const AnswerBlock = ({answer}: {answer: TeamFeedAnswer}) => {
  if (answer.kind === 'pulse') {
    return (
      <div className="flex flex-col gap-2">
        <Body2 kind="textBody" weight="semibold">
          {answer.prompt}
        </Body2>
        <div className="flex items-center gap-3">
          <span
            role="img"
            aria-label={answer.label}
            className="text-[32px] leading-none">
            {answer.emoji}
          </span>
          <Body1 kind="textDark" weight="semibold">
            {answer.label}
          </Body1>
        </div>
        {answer.feedback && <Body2 kind="textBody">{answer.feedback}</Body2>}
      </div>
    );
  }

  if (answer.kind === 'checklist') {
    return (
      <div className="flex flex-col gap-2">
        <Body2 kind="textBody" weight="semibold">
          {answer.prompt}
        </Body2>
        <div className="flex flex-col gap-2">
          {answer.items.map((item, index) => (
            <label
              key={`${answer.id}-${index}`}
              className="flex items-center gap-2 text-sm text-textBody">
              <input type="checkbox" checked={item.completed} readOnly />
              <span>{item.text}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (answer.kind === 'goals') {
    return (
      <div className="flex flex-col gap-3">
        <Body2 kind="textBody" weight="semibold">
          {answer.prompt}
        </Body2>
        <GoalReflectionResponse response={answer.response} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Body2 kind="textBody" weight="semibold">
        {answer.prompt}
      </Body2>
      <CustomMarkdown
        text={answer.response}
        compact
        className="text-[14px] leading-[22px] text-blackTwo"
      />
    </div>
  );
};

export const TeamFeedResponseCard = ({
  submission,
  mentionableUserIds,
  detail,
  cycle,
  isLoadingDetail = false,
  detailError = false,
  canManageResponses = false,
  onRetryDetail,
  defaultCommentsOpen = false,
}: TeamFeedResponseCardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAllAnswers, setShowAllAnswers] = useState(false);
  const answers = useMemo(() => buildTeamFeedAnswers(detail, cycle), [
    cycle,
    detail,
  ]);
  const visibleAnswers = showAllAnswers ? answers : answers.slice(0, 1);
  const remainingAnswers = Math.max(answers.length - 1, 0);
  const userId =
    typeof submission.user === 'object' ? submission.user?.id : submission.user;
  const userName = parseName(submission.user);
  const jobTitle =
    typeof submission.user === 'object'
      ? submission.user?.jobTitle || 'Team member'
      : 'Team member';
  const submittedAt = dayjs(
    submission.submittedAt || submission.createdAt,
  ).format('MMM D, h:mm A');
  const cycleId =
    cycle?.id ||
    (typeof submission.cycle === 'object'
      ? submission.cycle?.id
      : submission.cycle) ||
    submission.cycleId;
  const sendDate = (submission.createdAt || submission.updatedAt || '').slice(
    0,
    10,
  );
  const historyParams = new URLSearchParams();
  if (cycleId) historyParams.set('cycleId', cycleId);
  if (sendDate) historyParams.set('send', sendDate);
  historyParams.set('comments', submission.id || '');
  const teamFeedParams = new URLSearchParams(historyParams);
  teamFeedParams.set('view', 'teamFeed');
  const defaultCommentHref = location.pathname.includes(
    '/team-management/cycles/',
  )
    ? `${location.pathname}${location.search}`
    : `/checkins/my-checkins?${teamFeedParams.toString()}`;
  const overflowMenu = (handleClose: () => void) => (
    <div className="flex flex-col">
      <DropdownItem
        onClick={() => {
          if (cycle?.id) {
            const params = new URLSearchParams();
            params.set('returnTo', `${location.pathname}${location.search}`);
            navigate(
              `/checkins/team-management/cycles/${
                cycle.id
              }/participants/${userId}?${params.toString()}`,
            );
          } else {
            navigate(`/view-checkins/${submission.id}/direct`, {
              state: {user: userId},
            });
          }
          handleClose();
        }}>
        View submission
      </DropdownItem>
    </div>
  );

  if (!submission.id || !userId) return null;

  return (
    <article className="rounded-[10px] border border-borderLight bg-white">
      <header className="flex flex-col gap-3 border-b border-borderLight px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar
            tooltip={true}
            userId={userId}
            name={userName}
            size="lg"
            timezone={
              typeof submission.user === 'object'
                ? submission.user?.timezone
                : undefined
            }
            src={
              typeof submission.user === 'object'
                ? submission.user?.avatar?.url
                : undefined
            }
          />
          <div className="min-w-0">
            <Body1 kind="textDark" weight="bold" className="truncate">
              {userName}
            </Body1>
            <Body2 kind="textBody" className="truncate">
              {jobTitle}
            </Body2>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <div className="rounded-lg bg-backgroundDark px-2 py-1">
            <Body2 kind="textBody" weight="semibold">
              Submitted {submittedAt}
            </Body2>
          </div>
          {canManageResponses && (
            <DropdownVertical
              buttonAriaLabel={`Actions for ${userName}`}
              collapseOnClick={true}
              customIcon={<MoreVerticalIcon2 />}
              dropdownWrapperStyle={{right: 0}}
              menu={overflowMenu}
            />
          )}
        </div>
      </header>

      <div className="flex flex-col gap-5 px-4 py-5 sm:px-6">
        {isLoadingDetail ? (
          <div className="flex justify-center py-5">
            <ItemLoader />
          </div>
        ) : detailError ? (
          <div className="flex items-center justify-between gap-4 rounded-lg bg-backgroundDark p-4">
            <Body2 kind="textBody">Unable to load this response.</Body2>
            <Button kind="secondary" onClick={onRetryDetail}>
              Retry
            </Button>
          </div>
        ) : visibleAnswers.length ? (
          visibleAnswers.map((answer) => (
            <AnswerBlock key={`${answer.kind}-${answer.id}`} answer={answer} />
          ))
        ) : (
          <Body2 kind="textBody">
            No responses captured for this check-in.
          </Body2>
        )}

        {remainingAnswers > 0 && (
          <button
            type="button"
            aria-expanded={showAllAnswers}
            onClick={() => setShowAllAnswers((expanded) => !expanded)}
            className="w-fit text-sm font-medium text-purple300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-purple300">
            {showAllAnswers
              ? 'Show fewer answers'
              : `+ ${remainingAnswers} more ${
                  remainingAnswers === 1 ? 'answer' : 'answers'
                }`}
          </button>
        )}
      </div>

      <TeamFeedComments
        submissionId={submission.id}
        userId={userId}
        mentionableUserIds={mentionableUserIds}
        defaultOpen={defaultCommentsOpen}
        notificationContext={{
          cycleId,
          cycleName: cycle?.cycleName || submission.cycleName,
          submissionId: submission.id,
          participantId: userId,
          ownerHref: `/checkins/history?${historyParams.toString()}`,
          defaultHref: defaultCommentHref,
        }}
      />
    </article>
  );
};
